/**
 * /promoters — Public landing page (Phase P2)
 * ─────────────────────────────────────────────────────────────────────────
 * Premium dark + gold marketing page. Public. CTA opens the invite modal
 * which gates entry into the `/promoters/apply` multi-step form.
 *
 * Sections:
 *   1. Hero            — "Turn Influence Into Income"
 *   2. How It Works    — 3-step flow
 *   3. Invite-gated CTA modal
 *
 * Note: the public Earnings Model + Worked Example sections were intentionally
 * removed — commission rates are surfaced only inside the promoter dashboard
 * after approval, to keep commercial terms behind the NDA wall.
 */
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { promoterApi } from '../lib/api';
import { toast } from 'sonner';
import {
  Share2, Users, BadgeIndianRupee, Sparkles, ArrowRight, Lock, Wallet, LogIn,
} from 'lucide-react';

function HowStep({ n, title, desc, icon: Icon }) {
  return (
    <div className="flex-1 min-w-[240px] p-5 border border-[#2A2A2A] rounded-xl bg-gradient-to-br from-[#161616] to-[#0F0F0F] hover:border-[#D4A843]/40 transition-colors duration-200">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#D4A843]/10 border border-[#D4A843]/30 flex items-center justify-center">
          <Icon className="text-[#D4A843]" size={18} />
        </div>
        <span className="text-[10px] uppercase tracking-[2px] text-[#6F6F6F]">Step {n}</span>
      </div>
      <h3 className="text-[#F2F2F2] text-lg font-medium mb-1">{title}</h3>
      <p className="text-[#B9B9B9] text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

function InviteGateDialog({ open, onClose }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async () => {
    const s = code.trim().toUpperCase();
    if (!s) { toast.error('Enter your invite code'); return; }
    setBusy(true);
    try {
      const { data } = await promoterApi.publicValidateInvite(s);
      if (data?.ok) {
        toast.success('Invite verified — let\'s get you onboarded');
        navigate(`/promoters/apply?invite=${encodeURIComponent(data.code)}`);
      }
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      const msg = err?.response?.data?.detail?.message || 'Invite check failed';
      if (code === 'RATE_LIMIT')        toast.error('Too many attempts. Try again in 10 min.');
      else if (code === 'INVALID_INVITE') toast.error('Unknown or expired invite code.');
      else                                toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111111] border-[#2A2A2A] max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#F2F2F2] font-['EB_Garamond'] text-2xl">Enter your Invite Code</DialogTitle>
          <DialogDescription className="text-[#B9B9B9]">
            We work with a curated list of promoters. Got your invite from our team? Drop the code below.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="INV-XXXXX"
            className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2F2F2] uppercase tracking-widest text-center font-mono"
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            data-testid="invite-code-input"
          />
          <Button
            onClick={submit}
            disabled={busy}
            className="w-full bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-medium"
            data-testid="invite-validate-button"
          >
            {busy ? 'Checking…' : 'Continue'} <ArrowRight size={16} className="ml-2" />
          </Button>
          <p className="text-[10px] text-[#6F6F6F] text-center">
            Don&apos;t have an invite? Reach out to <a href="mailto:promoters@scratchjsr.com" className="text-[#D4A843] hover:underline">promoters@scratchjsr.com</a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PromotersLandingPage() {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen text-[#F2F2F2]" style={{ backgroundColor: '#0A0A0A' }}>
      <Helmet>
        <title>Become a Scratch Promoter | Turn Influence Into Income — Jamshedpur</title>
        <meta
          name="description"
          content="Join Scratch Unleashed's invite-only promoter network in Jamshedpur. Earn commissions on table bookings and event passes you refer. Apply with your invite code."
        />
        <link rel="canonical" href="https://scratchjsr.com/promoters" />
        <meta property="og:title" content="Become a Scratch Promoter" />
        <meta
          property="og:description"
          content="Invite-only nightclub promoter program in Jamshedpur. Turn your network into commissions on every booking."
        />
        <meta property="og:url" content="https://scratchjsr.com/promoters" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Top bar — returning promoter sign-in */}
      <div className="absolute top-0 right-0 z-20 px-4 sm:px-6 pt-5">
        <Link
          to="/account/promoter/login"
          data-testid="promoter-login-topbar"
          className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full border border-[#2A2A2A] hover:border-[#D4A843]/50 bg-black/40 backdrop-blur-sm text-[#B9B9B9] hover:text-[#F1E2B8] text-xs font-medium tracking-wide transition-colors"
        >
          <LogIn size={13} />
          Promoter Sign-in
        </Link>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[radial-gradient(ellipse_at_center,rgba(212,168,67,0.18),transparent_60%)]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 pt-20 pb-16 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D4A843]/30 bg-[#D4A843]/5 mb-6">
            <Sparkles size={12} className="text-[#D4A843]" />
            <span className="text-[10px] uppercase tracking-[2px] text-[#D4A843]">Invite-only program</span>
          </div>
          <h1 className="font-['EB_Garamond'] text-5xl md:text-6xl leading-tight font-semibold mb-4">
            Turn <span className="text-[#D4A843]">Influence</span> Into Income
          </h1>
          <p className="text-[#B9B9B9] text-lg max-w-2xl mx-auto leading-relaxed mb-8">
            Earn up to <b className="text-[#F2F2F2]">20%</b> commission on every guest you bring to Scratch.
            Branded link, transparent ledger, wallet-based payouts.
          </p>
          <Button
            onClick={() => setOpen(true)}
            className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-medium text-base px-8 h-12"
            data-testid="apply-now-hero-button"
          >
            Apply Now <ArrowRight size={18} className="ml-2" />
          </Button>
          <p className="mt-4 text-[12px] text-[#7A7A72]">
            Already a promoter?{' '}
            <Link
              to="/account/promoter/login"
              data-testid="promoter-login-hero-link"
              className="text-[#D4A843] hover:text-[#F1E2B8] underline underline-offset-4 transition-colors"
            >
              Sign in with WhatsApp
            </Link>
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-center font-['EB_Garamond'] text-3xl md:text-4xl mb-3">How it works</h2>
        <p className="text-center text-[#B9B9B9] text-sm mb-10 max-w-xl mx-auto">Three steps. No paperwork after onboarding. You see every rupee in real time.</p>
        <div className="flex flex-wrap gap-4">
          <HowStep n="1" icon={Share2} title="Share your exclusive link"    desc="We generate a branded link the moment your application is approved." />
          <HowStep n="2" icon={Users}  title="Guests book using your code"   desc="Attribution is sticky for 30 days; you don't lose credit on slow bookers." />
          <HowStep n="3" icon={Wallet} title="Earn commission on bookings"  desc="Commission lands in your wallet daily. Withdraw anytime over ₹5,000." />
        </div>
      </section>

      {/* CTA + footer */}
      <section className="max-w-5xl mx-auto px-6 py-20 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#2A2A2A] bg-[#0F0F0F] mb-6">
          <Lock size={12} className="text-[#B9B9B9]" />
          <span className="text-[10px] uppercase tracking-[2px] text-[#B9B9B9]">KYC + NDA · Mandatory before approval</span>
        </div>
        <h2 className="font-['EB_Garamond'] text-3xl md:text-4xl mb-3">Ready to start earning?</h2>
        <p className="text-[#B9B9B9] mb-6 max-w-xl mx-auto">Have your PAN + bank details handy. Onboarding takes &lt; 5 minutes.</p>
        <Button onClick={() => setOpen(true)} className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08] font-medium text-base px-8 h-12" data-testid="apply-now-footer-button">
          <BadgeIndianRupee size={18} className="mr-2" /> Apply Now
        </Button>
      </section>

      <InviteGateDialog open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
